import type {AppError, Result} from '@packages/utils';
import {type UseMutationResult, useMutation} from '@tanstack/react-query';
import {z} from 'zod';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import type {PostRemindPassSchema} from '../entities/remindpass/schema';

export const PostRemindPassResponse = z.string();
export type PostRemindPassResponse = z.infer<typeof PostRemindPassResponse>;
type ReturnPostValueType = Result<PostRemindPassResponse, AppError>;

export type PostRemindPassDataType = z.infer<typeof PostRemindPassSchema>;

type PostRemindPassSettingsArgs = {
  data: PostRemindPassDataType;
  version?: string;
};

const postRemindPass = async ({
  data,
  version = DEFAULT_API_VERSION,
}: PostRemindPassSettingsArgs): Promise<ReturnPostValueType> => {
  const path = `/${version}/users/password/remind`;

  const body = {
    email: data.email,
    lastName: data.lastName,
    firstName: data.firstName,
    birthDay: `${data.year}-${data.month.padStart(2, '0')}-${data.day.padStart(2, '0')}`,
  };
  const result: Result<PostRemindPassResponse, AppError> =
    await defaultClient.post(path, PostRemindPassResponse, body);
  if (!result?.ok) {
    throw new Error(result.error);
  }

  return result;
};

type PostRemindPassType = {
  onSuccessFnc: () => void;
  onErrorFnc: (error: AppError) => void;
};

export const usePostRemindPass = ({
  onSuccessFnc,
  onErrorFnc,
}: PostRemindPassType): UseMutationResult<
  ReturnPostValueType,
  AppError,
  PostRemindPassDataType
> => {
  return useMutation(
    async (data) => {
      return postRemindPass({data});
    },
    {
      onSuccess: onSuccessFnc,
      onError: onErrorFnc,
    },
  );
};
